import { Ingredient } from '../Types/api';

// Common fractions for nice formatting
const COMMON_FRACTIONS: Record<number, string> = {
  0.125: '1/8',
  0.25: '1/4',
  0.333: '1/3',
  0.375: '3/8',
  0.5: '1/2',
  0.625: '5/8',
  0.667: '2/3',
  0.75: '3/4',
  0.875: '7/8',
};

// Tolerance for matching common fractions
const FRACTION_TOLERANCE = 0.02;

/**
 * Parse a fraction string (e.g., "1/2", "3/4") to a number
 */
function parseFraction(str: string): number | null {
  const parts = str.split('/');
  if (parts.length !== 2) return null;

  const numerator = parseFloat(parts[0].trim());
  const denominator = parseFloat(parts[1].trim());

  if (isNaN(numerator) || isNaN(denominator) || denominator === 0) {
    return null;
  }

  return numerator / denominator;
}

/**
 * Parse an amount string to a number
 * Handles: "2", "1.5", "1/2", "2 1/2", "2-3" (returns average)
 */
export function parseAmount(str: string): number | null {
  if (!str || typeof str !== 'string') return null;

  const trimmed = str.trim();
  if (!trimmed) return null;

  // Handle ranges like "2-3" or "2 - 3" (return average)
  if (trimmed.includes('-') && !trimmed.startsWith('-')) {
    const rangeParts = trimmed.split('-').map((p) => p.trim());
    if (rangeParts.length === 2) {
      const low = parseAmount(rangeParts[0]);
      const high = parseAmount(rangeParts[1]);
      if (low !== null && high !== null) {
        return (low + high) / 2;
      }
    }
  }

  // Handle mixed fractions like "2 1/2" or "1 3/4"
  const mixedMatch = trimmed.match(/^(\d+)\s+(\d+\/\d+)$/);
  if (mixedMatch) {
    const whole = parseFloat(mixedMatch[1]);
    const fraction = parseFraction(mixedMatch[2]);
    if (!isNaN(whole) && fraction !== null) {
      return whole + fraction;
    }
  }

  // Handle simple fractions like "1/2"
  if (trimmed.includes('/')) {
    return parseFraction(trimmed);
  }

  // Handle decimals and whole numbers
  const num = parseFloat(trimmed);
  return isNaN(num) ? null : num;
}

/**
 * Find the closest common fraction for a decimal
 */
function findClosestFraction(decimal: number): string | null {
  for (const [value, fraction] of Object.entries(COMMON_FRACTIONS)) {
    if (Math.abs(decimal - parseFloat(value)) < FRACTION_TOLERANCE) {
      return fraction;
    }
  }
  return null;
}

/**
 * Format a number back to a readable amount string
 * Converts decimals to nice fractions when possible
 */
export function formatAmount(num: number): string {
  if (num <= 0) return '0';

  const whole = Math.floor(num);
  const decimal = num - whole;

  // If it's effectively a whole number (very close to an integer)
  if (decimal < FRACTION_TOLERANCE && num >= 1) {
    return whole.toString();
  }

  // For numbers less than 1 that are very small decimals, format as decimal
  if (whole === 0 && decimal < 0.1) {
    const rounded = Math.round(num * 100) / 100;
    return rounded.toString();
  }

  // Try to find a matching common fraction
  const fraction = findClosestFraction(decimal);

  if (fraction) {
    return whole > 0 ? `${whole} ${fraction}` : fraction;
  }

  // Fall back to decimal with reasonable precision
  // Round to 2 decimal places and remove trailing zeros
  const rounded = Math.round(num * 100) / 100;
  return rounded.toString();
}

export interface ScaledRecipe {
  servings: number;
  ingredients: Ingredient[];
  scaleFactor: number;
}

/**
 * Scale a single ingredient by a factor
 */
export function scaleIngredient(ingredient: Ingredient, scaleFactor: number): Ingredient {
  const parsedAmount = parseAmount(ingredient.amount);

  // If amount can't be parsed (e.g., "to taste"), return as-is
  if (parsedAmount === null) {
    return { ...ingredient };
  }

  const scaledAmount = parsedAmount * scaleFactor;
  return {
    ...ingredient,
    amount: formatAmount(scaledAmount),
  };
}

/**
 * Scale all ingredients by changing servings
 */
export function scaleByServings(
  originalServings: number,
  newServings: number,
  ingredients: Ingredient[],
): ScaledRecipe {
  if (originalServings <= 0 || newServings <= 0) {
    return {
      servings: originalServings,
      ingredients,
      scaleFactor: 1,
    };
  }

  const scaleFactor = newServings / originalServings;

  return {
    servings: newServings,
    ingredients: ingredients.map((ing) => scaleIngredient(ing, scaleFactor)),
    scaleFactor,
  };
}

/**
 * Scale all ingredients by changing one ingredient's amount
 * Returns new servings and scaled ingredients
 */
export function scaleByIngredient(
  originalServings: number,
  ingredients: Ingredient[],
  ingredientIndex: number,
  newAmount: number,
): ScaledRecipe {
  const originalIngredient = ingredients[ingredientIndex];
  if (!originalIngredient) {
    return {
      servings: originalServings,
      ingredients,
      scaleFactor: 1,
    };
  }

  const originalAmount = parseAmount(originalIngredient.amount);

  // If original amount can't be parsed, return unchanged
  if (originalAmount === null || originalAmount === 0) {
    return {
      servings: originalServings,
      ingredients,
      scaleFactor: 1,
    };
  }

  const scaleFactor = newAmount / originalAmount;
  const newServings = Math.round(originalServings * scaleFactor * 10) / 10; // Round to 1 decimal

  return {
    servings: newServings,
    ingredients: ingredients.map((ing) => scaleIngredient(ing, scaleFactor)),
    scaleFactor,
  };
}

/**
 * Check if an amount can be scaled (is numeric)
 */
export function isScalableAmount(amount: string): boolean {
  return parseAmount(amount) !== null;
}
